// src/context/FeatureContext.js
/**
 * FeatureContext - Per-company feature flag management with real-time sync
 *
 * Depends on: AuthContext (must be initialized first)
 * Consumed by: DataContext (waits for companies to load), App.js (FeatureProtectedRoute)
 *
 * Feature flags are stored in Firestore `companies/{companyId}.features` array.
 * Uses real-time listeners (onSnapshot) so flag changes apply instantly without reload.
 *
 * MULTI-COMPANY RULE: If admin manages multiple companies and ANY company has a
 * feature enabled, the admin sees that feature. This allows partial rollout per-company
 * while giving multi-company admins a unified view.
 *
 * Current flags:
 *   - delivery: Enables Vehicles and Delivery Receipts pages
 *
 * Also provides `companies` array used by DataContext and other parts of the app.
 */
import React, { createContext, useState, useContext, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const FeatureContext = createContext();

export function useFeatures() {
  return useContext(FeatureContext);
}

export function FeatureProvider({ children }) {
  const { user } = useAuth();
  const [features, setFeatures] = useState({
    delivery: false,
    // Add other features here as needed
  });
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !user.adminCompanyIds || user.adminCompanyIds.length === 0) {
      setLoading(false);
      return;
    }

    const adminCompanyIds = user.adminCompanyIds;
    const unsubscribes = [];
    // Accumulates company data from all listeners; updated on each snapshot callback
    const companiesData = {};

    /**
     * Re-evaluates feature flags across all loaded companies.
     * Called every time any company snapshot updates, so features stay in sync.
     */
    const updateFeaturesFromCompanies = () => {
      const allCompanies = Object.values(companiesData);

      // OR-logic: feature enabled if ANY admin company has it
      const hasDeliveryFeature = allCompanies.some(
        company => company.features && Array.isArray(company.features) && company.features.includes('delivery')
      );

      setFeatures({
        delivery: hasDeliveryFeature
        // Add other features here as needed
      });
      setCompanies(allCompanies);
      setLoading(false);
    };

    // Set up a separate real-time listener for each company document.
    // Each listener fires independently; any change triggers full re-evaluation.
    for (const companyId of adminCompanyIds) {
      const unsubscribe = onSnapshot(
        doc(db, 'companies', companyId),
        (companyDoc) => {
          if (companyDoc.exists()) {
            companiesData[companyId] = {
              id: companyDoc.id,
              ...companyDoc.data()
            };
          } else {
            delete companiesData[companyId];
          }
          updateFeaturesFromCompanies();
        },
        (error) => {
          console.error(`Error listening to company ${companyId}:`, error);
        }
      );
      unsubscribes.push(unsubscribe);
    }

    // Cleanup: unsubscribe all listeners on unmount or user change
    return () => {
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
  }, [user]);

  const value = {
    features,
    companies,
    loading,
  };

  return (
    <FeatureContext.Provider value={value}>
      {children}
    </FeatureContext.Provider>
  );
}